import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  StyledButton,
  StyledIconButton,
  StyledTextButton,
} from "../../styles/ButtonStyles";
import {
  StyledFlexColumn,
  StyledFlexItem,
  StyledFlexRow,
} from "../../styles/LayoutStyles";
import {
  StyledLoginAnnouncementCard,
  StyledLoginAnnouncementsHeader,
  StyledLoginAnnouncementsOverlay,
  StyledLoginLogo,
  StyledLoginWidthConstraint,
  StyledQRCodeWrapper,
} from "./LoginPageStyles";
import { faBell, faClose } from "@fortawesome/free-solid-svg-icons";
import { useLanguageContext } from "../../context/LanguageContext";
import { useTheme } from "styled-components";
import InputWrapper from "../../components/input/wrapper/InputWrapper";
import { StyledInput } from "../../styles/InputStyles";
import QRCode from "qrcode.react";
import {
  StyledCard,
  StyledCardBody,
  StyledCardHeader,
} from "../../styles/CardStyles";
import HiddenLoader from "../../components/loader/HiddenLoader/HiddenLoader";
import EVAApiHelper from "../../helpers/EVAApiHelper";
import ThemeSelect from "../../components/select/custom/theme/ThemeSelect";
import Version from "../../components/version/Version";
import Checkbox from "../../components/input/checkbox/Checkbox";
import LanguageSelect from "../../components/select/custom/language/LanguageSelect";
import Form from "../../components/form/Form";

function LoginPageView({
  loginData,
  discoverData,
  announcements,
  onEmailChange,
  onPasswordChange,
  onTFACodeChange,
  onRememberMeChange,
  onLoginWithSSOChange,
  onQRCodeClick,
  onSubmit,
  onDiscover,
  onRequestPasswordReset,
  submitError,
  showAnnouncements,
  setShowAnnouncements,
}) {
  const theme = useTheme();
  const { translate } = useLanguageContext();

  return (
    <>
      <StyledLoginWidthConstraint>
        <StyledLoginLogo src={`/assets/images/${theme.logo}`} alt="EVA Logo" />

        <StyledFlexColumn>
          <StyledCard>
            <StyledCardHeader>
              <StyledFlexRow style={{ alignItems: "center" }}>
                <StyledFlexItem>
                  <h2>{translate("eva.main.auth.login")}</h2>
                </StyledFlexItem>

                <HiddenLoader groups="login_page_announcements_fetch">
                  {announcements.length > 0 ? (
                    <StyledTextButton
                      title={translate("eva.main.entities.announcements")}
                      color={theme.colors.accent.normal}
                      onClick={() => setShowAnnouncements(true)}
                    >
                      <StyledFlexRow style={{ gap: "5px" }}>
                        <FontAwesomeIcon icon={faBell} size="lg" />
                        <small>({announcements.length})</small>
                      </StyledFlexRow>
                    </StyledTextButton>
                  ) : (
                    ""
                  )}
                </HiddenLoader>

                <LanguageSelect />

                <ThemeSelect />
              </StyledFlexRow>
            </StyledCardHeader>

            <StyledCardBody>
              <Form
                onSubmit={discoverData ? onSubmit : onDiscover}
                loadingGroups={["login", "discover"]}
              >
                <StyledFlexColumn
                  style={{ gap: theme.spacing.large, alignItems: "flex-start" }}
                >
                  <StyledFlexColumn style={{ width: "100%" }}>
                    <InputWrapper
                      for="email"
                      label={translate("eva.main.validation.attributes.email")}
                      required
                      error={EVAApiHelper.getResponseError(
                        "email",
                        submitError,
                      )}
                    >
                      <StyledInput
                        type="text"
                        id="email"
                        name="email"
                        value={loginData.email}
                        onChange={onEmailChange}
                        autoComplete="username"
                        placeholder={translate(
                          "eva.main.validation.placeholders.email",
                        )}
                      />
                    </InputWrapper>

                    {discoverData && !loginData.loginWithSSO && (
                      <InputWrapper
                        for="password"
                        label={translate(
                          "eva.main.validation.attributes.password",
                        )}
                        required
                        error={EVAApiHelper.getResponseError(
                          "password",
                          submitError,
                        )}
                      >
                        <StyledInput
                          type="password"
                          id="password"
                          name="password"
                          value={loginData.password}
                          onChange={onPasswordChange}
                          autoComplete="current-password"
                          placeholder={translate(
                            "eva.main.validation.placeholders.password",
                          )}
                        />
                      </InputWrapper>
                    )}

                    {discoverData?.user?.use_tfa && !loginData.loginWithSSO && (
                      <InputWrapper
                        for="tfa_code"
                        label={translate(
                          "eva.main.validation.attributes.tfa_code",
                        )}
                        required
                        error={EVAApiHelper.getResponseError(
                          "tfa_code",
                          submitError,
                        )}
                      >
                        <StyledInput
                          type="text"
                          id="tfa_code"
                          name="tfa_code"
                          value={loginData.tfaCode}
                          onChange={onTFACodeChange}
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          placeholder={translate(
                            "eva.main.validation.placeholders.code.tfa",
                          )}
                        />
                      </InputWrapper>
                    )}

                    <StyledFlexRow>
                      <InputWrapper
                        label={translate("eva.main.auth.remember_me")}
                        style={{ flex: 1 }}
                      >
                        <Checkbox
                          value={loginData.rememberMe}
                          onChange={onRememberMeChange}
                        />
                      </InputWrapper>

                      {discoverData?.azure_config?.active && (
                        <InputWrapper
                          label={translate("eva.main.auth.login_with_sso")}
                          style={{ flex: 1 }}
                        >
                          <Checkbox
                            value={loginData.loginWithSSO}
                            onChange={onLoginWithSSOChange}
                          />
                        </InputWrapper>
                      )}
                    </StyledFlexRow>
                  </StyledFlexColumn>

                  <StyledFlexColumn>
                    {/* Submit button */}
                    <HiddenLoader
                      groups={["login", "discover"]}
                      containerStyle={{ display: "inline-block" }}
                    >
                      <StyledButton type="submit">
                        {translate("eva.main.general.proceed")}
                      </StyledButton>
                    </HiddenLoader>

                    {/* Password reset */}
                    <HiddenLoader
                      groups={["request_password_reset"]}
                      containerStyle={{ display: "inline-block" }}
                    >
                      <StyledTextButton
                        style={{ color: theme.colors.accent.normal }}
                        onClick={onRequestPasswordReset}
                      >
                        {translate("eva.main.auth.password_reset.question")}
                      </StyledTextButton>
                    </HiddenLoader>
                  </StyledFlexColumn>
                </StyledFlexColumn>
              </Form>
            </StyledCardBody>
          </StyledCard>

          {/* TFA Code info */}
          {discoverData?.tfa_qr_code_url && (
            <StyledCard>
              <StyledCardBody>
                <StyledFlexRow
                  style={{ alignItems: "center", flexWrap: "nowrap" }}
                >
                  <StyledQRCodeWrapper>
                    <QRCode
                      onClick={onQRCodeClick}
                      value={discoverData.tfa_qr_code_url}
                    />
                  </StyledQRCodeWrapper>
                  <p>{translate("eva.main.auth.tfa.info")}</p>
                </StyledFlexRow>

                <StyledFlexRow
                  style={{
                    alignItems: "center",
                    margin: `${theme.spacing.large} 0px 0px 0px`,
                    flexWrap: "nowrap",
                  }}
                >
                  <a
                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/assets/images/google_play_button.png"
                      alt="Google Play"
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="/assets/images/apple_store_button.png"
                      alt="Apple Store"
                    />
                  </a>
                </StyledFlexRow>
              </StyledCardBody>
            </StyledCard>
          )}
        </StyledFlexColumn>
      </StyledLoginWidthConstraint>

      <Version fixed={true} />

      {/* Announcements overlay display */}
      {showAnnouncements ? (
        <StyledLoginAnnouncementsOverlay>
          <StyledLoginWidthConstraint>
            {/* Announcements header */}
            <StyledLoginAnnouncementsHeader>
              <StyledFlexRow style={{ alignItems: "center" }}>
                <FontAwesomeIcon icon={faBell} />

                <StyledFlexItem>
                  <h1>{translate("eva.main.entities.announcements")}</h1>
                </StyledFlexItem>

                <StyledIconButton
                  title={translate("eva.main.general.back")}
                  onClick={() => setShowAnnouncements(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </StyledIconButton>
              </StyledFlexRow>
            </StyledLoginAnnouncementsHeader>

            {/* Announcements */}
            <StyledFlexColumn style={{ gap: theme.spacing.large }}>
              {announcements.map((announcement, index) => (
                <StyledLoginAnnouncementCard key={index}>
                  <StyledCardHeader>
                    <h2>{announcement.title}</h2>
                  </StyledCardHeader>

                  <StyledCardBody
                    dangerouslySetInnerHTML={{
                      __html: announcement.description,
                    }}
                  />
                </StyledLoginAnnouncementCard>
              ))}
            </StyledFlexColumn>
          </StyledLoginWidthConstraint>
        </StyledLoginAnnouncementsOverlay>
      ) : (
        ""
      )}
    </>
  );
}

export default LoginPageView;
